import React from "react";

/**
 * Animated SVG circular progress ring used across the Learning Hub.
 *
 * The status of a resource maps to a discrete percentage so the ring is
 * always "right" even when we don't have a continuous progress signal:
 *   not_started → 0
 *   in_progress → 50
 *   completed   → 100
 *   skipped     → faded, struck-through
 *
 * For collection-level usage, pass `value` (0..100) directly.
 */
export const STATUS_TO_PCT = {
    not_started: 0,
    in_progress: 50,
    completed: 100,
    skipped: 0,
};

export const STATUS_COLOR = {
    not_started: { stroke: "#CBD5E1", track: "#E2E8F0" },         // slate
    in_progress: { stroke: "#2EB4AC", track: "#E2F5F3" },         // teal
    completed:   { stroke: "#10B981", track: "#D1FAE5" },         // emerald
    skipped:     { stroke: "#A78BFA", track: "#EDE9FE" },         // violet (faded)
};

const ProgressRing = ({
    value,                  // optional continuous 0..100; if omitted, computed from `status`
    status = "not_started", // not_started | in_progress | completed | skipped
    size = 44,
    stroke = 4,
    showLabel = true,
    label,                  // override the in-center label (string)
    children,               // optional center slot (e.g. icon)
    className = "",
    "data-testid": testId,
}) => {
    const pct = value != null ? Math.max(0, Math.min(100, value)) : STATUS_TO_PCT[status] ?? 0;
    const colors = STATUS_COLOR[status] || STATUS_COLOR.not_started;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (pct / 100) * c;
    const isComplete = pct >= 100 || status === "completed";
    const isSkipped = status === "skipped";

    return (
        <div
            className={`relative inline-flex items-center justify-center ${className}`}
            style={{ width: size, height: size }}
            data-testid={testId}
        >
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke={colors.track}
                    strokeWidth={stroke}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke={colors.stroke}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={c}
                    strokeDashoffset={offset}
                    style={{ transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1), stroke 200ms ease" }}
                />
            </svg>
            <div className={`absolute inset-0 flex items-center justify-center ${isSkipped ? "opacity-50" : ""}`}>
                {children ? (
                    children
                ) : (
                    showLabel && (
                        <span className={`font-mono font-bold tabular-nums text-[10px] ${
                            isComplete ? "text-emerald-600" : isSkipped ? "text-violet-500" : pct > 0 ? "text-[#1F7F79]" : "text-slate-500"
                        }`}>
                            {label != null ? label : `${Math.round(pct)}%`}
                        </span>
                    )
                )}
            </div>
        </div>
    );
};

export default ProgressRing;
